import { DiagnosticOutDTO, RobotActionOutDTO, RobotSuggestionRefusalOutDTO } from '@api/ringil-api.v2';
import { MESSAGE_DEFAULTS } from './messageDefaults';

/**
 * The backend does not translate.
 *
 * Diagnostics and robot-action summaries arrive as a Locize key plus positional parameters, and
 * the parameters carry the rule's own text verbatim — a fact name, an operator, a unit — because
 * the DSL is never translated, only what we say about it.
 */
type Translate = (key: string, defaultText?: string, data?: unknown) => string;

const APPEND_NOTE_TO_PREFIX = 'aidispatcher.action.append_note_to_';

/** `{0}` is `params[0]`, so the interpolation object is keyed by position. */
export const positionalParams = (params?: string[]): Record<string, string> => Object.fromEntries((params ?? []).map((value, index) => [String(index), value]));

/**
 * Renders a key, with our own English wording standing in until Locize has it.
 *
 * Locize is filled in separately from the backend release, so a missing key is a normal transient
 * state rather than a bug — but "key: 45000 40000" is not an answer to *why was nothing awarded*.
 * `MESSAGE_DEFAULTS` puts the parameters back into a sentence, and the last resort — the key's own
 * context plus its parameters — is kept for a message this release has never heard of, where the
 * values (`expedition.maxWeight`, `>`, `12 t`) still say which part of the rule is meant.
 */
const withFallback = (tr: Translate, key: string, params: string[], context: string): string => {
  const readable = readableParams(tr, key, params ?? []);
  const values = readable.filter(value => value !== null && value !== undefined && value !== '');
  const lastResort = values.length > 0 ? `${context}: ${values.join(' ')}` : context;
  return tr(key, MESSAGE_DEFAULTS[key] ?? lastResort, positionalParams(readable));
};

/**
 * Message parameters that are codes with a label of their own elsewhere in the app, by position.
 * `extra_cost_added` carries the extra cost type as its code (`FUEL_SURCHARGE`), and the backend's
 * key list says to render it through the same `extra_cost.enum.*` key the type picker uses.
 */
const PARAM_LABELS: Record<string, Record<number, (tr: Translate, value: string) => string>> = {
  'aidispatcher.action.detail.extra_cost_added': { 0: (tr, value) => extraCostTypeLabel(tr, value) },
  // Which note, `INTERNAL` or `CARRIER` — labelled like the `to` picker of `append_note`.
  'aidispatcher.action.detail.note_appended': { 0: (tr, value) => enumValueLabel(tr, value, APPEND_NOTE_TO_PREFIX) },
  'aidispatcher.action.detail.note_unchanged': { 0: (tr, value) => enumValueLabel(tr, value, APPEND_NOTE_TO_PREFIX) },
};

const readableParams = (tr: Translate, key: string, params: string[]): string[] => {
  const labels = PARAM_LABELS[key];
  if (!labels) return params;
  return params.map((value, index) => (labels[index] && value ? labels[index](tr, value) : value));
};

export const diagnosticMessage = (tr: Translate, diagnostic: DiagnosticOutDTO): string =>
  withFallback(tr, diagnostic.messageKey, diagnostic.params, String(diagnostic.diagnosticCode));

export const diagnosticHint = (tr: Translate, diagnostic: DiagnosticOutDTO): string | null =>
  diagnostic.hintKey ? withFallback(tr, diagnostic.hintKey, diagnostic.hintParams, String(diagnostic.diagnosticCode)) : null;

export const actionDetail = (tr: Translate, action: RobotActionOutDTO): string | null =>
  action.detailKey ? withFallback(tr, action.detailKey, action.detailParams, action.action) : null;

export const actionError = (tr: Translate, action: RobotActionOutDTO): string | null =>
  action.errorKey ? withFallback(tr, action.errorKey, action.errorParams, action.action) : null;

/**
 * The execution-detail endpoint returns the raw stored action JSON, where the message is the
 * `ActionMessage` **enum name** rather than the Locize key the feed maps it to. The keys follow
 * `aidispatcher.action.{detail,error}.<lower_snake_name>`, so the key is derivable — and when the
 * derivation is ever wrong, `withFallback` still shows the parameters, which carry the useful
 * values. Remove this once `/executions/{id}` maps the keys the way `/activity/feed` does.
 */
export const keyFromMessageName = (name: string | undefined, kind: 'detail' | 'error'): string | undefined =>
  name ? `aidispatcher.action.${kind}.${name.toLowerCase()}` : undefined;

/** One executed action, from either shape: the feed's mapped keys or the stored enum names. */
export interface ExecutedAction {
  action: string;
  ok: boolean;
  detailKey?: string;
  detail?: string;
  detailParams?: string[];
  errorKey?: string;
  error?: string;
  errorParams?: string[];
}

export const executedActionDetail = (tr: Translate, action: ExecutedAction): string | null => {
  const key = action.detailKey ?? keyFromMessageName(action.detail, 'detail');
  return key ? withFallback(tr, key, action.detailParams ?? [], action.action) : null;
};

export const executedActionError = (tr: Translate, action: ExecutedAction): string | null => {
  const key = action.errorKey ?? keyFromMessageName(action.error, 'error');
  return key ? withFallback(tr, key, action.errorParams ?? [], action.action) : null;
};

/**
 * A robot suggestion's refusal — the same `aidispatcher.action.error.*` keys the execution log
 * renders, arriving on the expedition detail instead. Rendered here rather than by a second copy
 * of the mapping.
 */
export const suggestionRefusalMessage = (tr: Translate, refusal: RobotSuggestionRefusalOutDTO): string =>
  withFallback(tr, refusal.messageKey, refusal.messageParams, refusal.messageKey);

/**
 * Action parameter names and fact groups are the only parts of the catalogue the metadata endpoint
 * does not give an `i18nKey` for — `ActionParamMetadataOutDTO` has no key at all, and
 * `FactMetadataOutDTO.group` is a bare string. Both are rendered as labels, so they go through
 * Locize under a derived key, with the raw catalogue name as the fallback: until a translation
 * exists the screen reads exactly as it did, and no release is needed to add one.
 */
export const paramLabel = (tr: Translate, name: string): string => tr(`AiDispatcher.param.${name}`, name);

export const factGroupLabel = (tr: Translate, group: string): string => tr(`AiDispatcher.factGroup.${group}`, group);

/** An action, by the catalogue's own key when metadata has not been consulted. */
export const actionLabel = (tr: Translate, key: string, i18nKey?: string): string => tr(i18nKey ?? `AiDispatcher.action.${key}`, key);

/**
 * One value of an enum fact or parameter — `NEW`, `ASSIGNING`, `ROAD`.
 *
 * Keyed on the value alone rather than on the fact that offers it, because the same value set is
 * reached from several facts (`expedition.status` and `expedition.previousStatus` are the obvious
 * pair) and a per-fact key would ask for the same word to be translated once per fact.
 *
 * The label is for reading only: the value written into the rule is always the raw enum name, so
 * nothing here can change what a rule means.
 */
export const enumValueLabel = (tr: Translate, value: string, keyPrefix = 'AiDispatcher.enum'): string => tr(enumValueKey(keyPrefix, value), value);

/** A prefix ending in `_` is joined directly (`append_note_to_` + `CARRIER`), any other with a dot. */
const enumValueKey = (keyPrefix: string, value: string): string => (keyPrefix.endsWith('_') ? `${keyPrefix}${value}` : `${keyPrefix}.${value}`);

export const enumValueOptions = (tr: Translate, values?: string[], keyPrefix?: string): { label: string; value: string }[] =>
  (values ?? []).map(value => ({ label: enumValueLabel(tr, value, keyPrefix), value }));

/**
 * Enum parameters whose values already have labels elsewhere in the app, keyed `action.param`.
 * `add_extra_cost`'s `type` is the extra cost code list the "Add extra cost" dialog offers, whose
 * labels live under `extra_cost.enum.*` — asking for `AiDispatcher.enum.FUEL_SURCHARGE` would have
 * every code translated a second time. Unlisted parameters keep the generic `AiDispatcher.enum.*`.
 */
const PARAM_ENUM_KEY_PREFIXES: Record<string, string> = {
  'add_extra_cost.type': 'extra_cost.enum',
  // The backend's key list names these `aidispatcher.action.append_note_to_{INTERNAL|CARRIER}`.
  'append_note.to': APPEND_NOTE_TO_PREFIX,
};

export const paramEnumKeyPrefix = (action: string | undefined, param: string): string | undefined =>
  action ? PARAM_ENUM_KEY_PREFIXES[`${action}.${param}`] : undefined;

export const extraCostTypeLabel = (tr: Translate, code: string): string => enumValueLabel(tr, code, PARAM_ENUM_KEY_PREFIXES['add_extra_cost.type']);
